// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import { GlobalStyles, alpha } from '@mui/material';

const AI_HIGHLIGHT_CLASS = 'ai-highlight-attribute';
const AI_HIGHLIGHT_DURATION = 3500;

const REDIRECTION_HIGHLIGHT_CLASS = 'nav-highlight-attribute';
const REDIRECTION_HIGHLIGHT_DURATION = 3500;

const getAIHighlightStyles = (theme) => {
  const warning20 = theme?.palette?.warning?.[20];

  return {
    '@keyframes aiAttributeHighlightPulse': {
      '0%': {
        boxShadow: `0 0 0 0 ${theme?.palette?.other?.aiHighlightShadowBase}`,
        backgroundColor: theme?.palette?.other?.aiHighlightBackgroundBase,
      },
      '15%': {
        boxShadow: `0 0 8px 2px ${theme?.palette?.other?.aiHighlightShadowStrong}`,
        backgroundColor: theme?.palette?.other?.aiHighlightBackgroundStrong,
      },
      '33%': {
        boxShadow: '0 0 0 0 transparent',
        backgroundColor: theme?.palette?.other?.aiHighlightBackgroundFaint,
      },
      '48%': {
        boxShadow: `0 0 8px 2px ${theme?.palette?.other?.aiHighlightShadowMedium}`,
        backgroundColor: theme?.palette?.other?.aiHighlightBackgroundMedium,
      },
      '66%': {
        boxShadow: '0 0 0 0 transparent',
        backgroundColor: theme?.palette?.other?.aiHighlightBackgroundFaint,
      },
      '81%': {
        boxShadow: `0 0 6px 1px ${theme?.palette?.other?.aiHighlightShadowSoft}`,
        backgroundColor: theme?.palette?.other?.aiHighlightBackgroundSoft,
      },
      '100%': {
        boxShadow: '0 0 0 0 transparent',
        backgroundColor: 'transparent',
      },
    },
    '@keyframes navAttributeHighlightPulse': {
      '0%': {
        boxShadow: 'inset 0 0 0 0 transparent, 0 0 0 0 transparent',
        backgroundColor: 'transparent',
      },
      '15%': {
        boxShadow: `inset 0 0 12px 3px ${alpha(warning20, 0.8)}, 0 0 12px 4px ${alpha(warning20, 0.6)}`,
        backgroundColor: warning20,
      },
      '33%': {
        boxShadow: 'inset 0 0 0 0 transparent, 0 0 0 0 transparent',
        backgroundColor: 'transparent',
      },
      '48%': {
        boxShadow: `inset 0 0 10px 2px ${alpha(warning20, 0.6)}, 0 0 10px 3px ${alpha(warning20, 0.5)}`,
        backgroundColor: warning20,
      },
      '66%': {
        boxShadow: 'inset 0 0 0 0 transparent, 0 0 0 0 transparent',
        backgroundColor: 'transparent',
      },
      '81%': {
        boxShadow: `inset 0 0 8px 2px ${alpha(warning20, 0.5)}, 0 0 8px 2px ${alpha(warning20, 0.4)}`,
        backgroundColor: warning20,
      },
      '100%': {
        boxShadow: 'inset 0 0 0 0 transparent, 0 0 0 0 transparent',
        backgroundColor: 'transparent',
      },
    },
    [`.${AI_HIGHLIGHT_CLASS}`]: {
      animation: `aiAttributeHighlightPulse ${AI_HIGHLIGHT_DURATION}ms ease-in-out`,
      borderRadius: '4px',
    },
    [`.${REDIRECTION_HIGHLIGHT_CLASS}`]: {
      animation: `navAttributeHighlightPulse ${REDIRECTION_HIGHLIGHT_DURATION}ms ease-in-out`,
      borderRadius: '4px',
    },
    // Ensure the whole portion of the attribute field is highlighted by making inner inputs/textarea backgrounds transparent during highlight
    [`.${REDIRECTION_HIGHLIGHT_CLASS} .MuiInputBase-root, .${REDIRECTION_HIGHLIGHT_CLASS} .MuiOutlinedInput-root, .${REDIRECTION_HIGHLIGHT_CLASS} input, .${REDIRECTION_HIGHLIGHT_CLASS} textarea, .${REDIRECTION_HIGHLIGHT_CLASS} .MuiSelect-select, .${REDIRECTION_HIGHLIGHT_CLASS} .MuiAutocomplete-inputRoot`]:
      {
        backgroundColor: 'transparent !important',
      },
  };
};

const useAIAttributeHighlight = () => {
  const aiHighlightStyles = useMemo(() => {
    return <GlobalStyles styles={getAIHighlightStyles} />;
  }, []);

  const highlightElement = useCallback((element) => {
    if (!element) {
      return;
    }

    element.classList.remove(AI_HIGHLIGHT_CLASS);

    // Force reflow via rAF so re-adding the class restarts the animation.
    requestAnimationFrame(() => {
      element.classList.add(AI_HIGHLIGHT_CLASS);

      const cleanup = () => {
        element.classList.remove(AI_HIGHLIGHT_CLASS);
      };

      element.addEventListener('animationend', cleanup, { once: true });
      // Fallback timeout.
      setTimeout(cleanup, AI_HIGHLIGHT_DURATION + 100);
    });
  }, []);

  const highlightNavigationElement = useCallback((element) => {
    if (!element) {
      return;
    }

    element.classList.remove(REDIRECTION_HIGHLIGHT_CLASS);

    // Force reflow via rAF so re-adding the class restarts the animation.
    requestAnimationFrame(() => {
      element.classList.add(REDIRECTION_HIGHLIGHT_CLASS);

      const cleanup = () => {
        element.classList.remove(REDIRECTION_HIGHLIGHT_CLASS);
      };

      element.addEventListener('animationend', cleanup, { once: true });
      // Fallback timeout.
      setTimeout(cleanup, REDIRECTION_HIGHLIGHT_DURATION + 100);
    });
  }, []);

  return { aiHighlightStyles, highlightElement, highlightNavigationElement };
};

export default useAIAttributeHighlight;
